import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../api/client'
import type { Event } from '../types'
import { Calendar, MapPin, Users, ArrowLeft, Ticket, ShoppingCart, Megaphone } from 'lucide-react'
import { useOpenConversation } from '../hooks/useOpenConversation'
import { isAfter } from 'date-fns'
import { formatDayDateToLocal, formatTimeRangeToLocal, getTimezoneAbbreviation } from '../utils/timezone'
import { useLocale } from '../contexts/LocaleContext'
import { useAuth } from '../contexts/AuthContext'
import ImageWithFallback from '../components/ImageWithFallback'

interface TicketPurchaseData {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  quantity: number
}

const EventDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const { t } = useLocale()
  const { user, isVolunteer, isAdmin } = useAuth()
  const canMessage = !!user && (isVolunteer || isAdmin)
  const { open: openChat, opening, error: chatError } = useOpenConversation()
  
  const [event, setEvent] = useState<Event | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [purchaseData, setPurchaseData] = useState<TicketPurchaseData>({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    quantity: 1
  })
  const [purchasing, setPurchasing] = useState(false)
  const [purchaseError, setPurchaseError] = useState('')

  const fetchEvent = useCallback(async () => {
    try {
      if (!eventId) return
      const data = await api.events.get(eventId)
      setEvent(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load event')
    } finally {
      setLoading(false)
    }
  }, [eventId])

  useEffect(() => {
    if (eventId) {
      fetchEvent()
    }
  }, [eventId, fetchEvent])

  const getEventStatus = (event: Event) => {
    const now = new Date()
    const startDate = new Date(event.start_date)
    const endDate = new Date(event.end_date)

    if (isAfter(startDate, now)) {
      return { text: t('events.statusUpcoming'), color: 'bg-blue-100 text-blue-800' }
    } else if (isAfter(endDate, now)) {
      return { text: t('events.statusOngoing'), color: 'bg-green-100 text-green-800' }
    } else {
      return { text: t('events.statusCompleted'), color: 'bg-gray-100 text-gray-800' }
    }
  }

  const handlePurchaseClick = () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/login?returnTo=/events/${eventId}`)
      return
    }
    setShowPurchaseModal(true)
  }

  const handlePurchaseSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPurchasing(true)
    setPurchaseError('')

    try {
      // Here you would integrate with your payment system
      // For now, we'll simulate a successful purchase
      
      // Update event capacity
      await api.events.update(eventId!, {
        current_capacity: (event?.current_capacity || 0) + purchaseData.quantity
      })

      // Create ticket record (you might want to create a tickets table)
      // For now, we'll just show success
      
      setShowPurchaseModal(false)
      alert(t('events.ticketPurchaseSuccess'))
      
      // Refresh event data
      fetchEvent()
    } catch (err: any) {
      setPurchaseError(err.message || t('events.ticketPurchaseError'))
    } finally {
      setPurchasing(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPurchaseData(prev => ({
      ...prev,
      [name]: name === 'quantity' ? parseInt(value) || 1 : value
    }))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || t('events.eventNotFound')}</p>
          <button
            onClick={() => navigate('/events')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {t('common.backToEvents')}
          </button>
        </div>
      </div>
    )
  }

  const eventStatus = getEventStatus(event)
  const isEventFull = event.current_capacity >= event.max_capacity
  const isEventPast = new Date(event.end_date) < new Date()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/events')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              {t('common.backToEvents')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-8">
          {/* Event Image */}
          <div className="mb-8 lg:mb-0">
            <ImageWithFallback
              src={event.image_url}
              alt={event.title}
              className="w-full h-96 rounded-lg object-cover"
              fallbackClassName="w-full h-96 rounded-lg bg-gray-200 flex items-center justify-center"
              fallbackIcon={<Calendar className="h-24 w-24 text-gray-400" />}
            />
          </div>

          {/* Event Details */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {event.title}
              </h1>
              <div className="flex items-center space-x-2 mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${eventStatus.color}`}>
                  {eventStatus.text}
                </span>
                {event.requires_ticket && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                    {t('events.requiresTicket')}
                  </span>
                )}
              </div>
            </div>

            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 text-lg leading-relaxed">
                {event.description}
              </p>
            </div>

            {/* Event Info */}
            <div className="space-y-4">
              <div className="flex items-center text-gray-600">
                <Calendar className="h-5 w-5 mr-3" />
                <div>
                  <p className="font-medium">
                    {formatDayDateToLocal(event.start_date, 'EEEE, MMMM d, yyyy')}
                    <span className="ml-2 text-sm text-gray-500">({getTimezoneAbbreviation()})</span>
                  </p>
                  <p className="text-sm">
                    {formatTimeRangeToLocal(event.start_date, event.end_date)}
                    <span className="ml-1 text-xs text-gray-400">({getTimezoneAbbreviation()})</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center text-gray-600">
                <MapPin className="h-5 w-5 mr-3" />
                <span>{event.location}</span>
              </div>

              <div className="flex items-center text-gray-600">
                <Users className="h-5 w-5 mr-3" />
                <span>
                  {event.current_capacity}/{event.max_capacity} {t('events.registered')}
                </span>
              </div>

              {event.price && (
                <div className="flex items-center text-gray-600">
                  <Ticket className="h-5 w-5 mr-3" />
                  <span className="font-medium text-lg">
                    €{event.price.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {canMessage && (
              <div className="pt-6">
                <button
                  type="button"
                  disabled={opening}
                  onClick={() => openChat(() => api.messaging.forEvent(event.id))}
                  className="flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                >
                  <Megaphone className="mr-2 h-5 w-5" />
                  {t('messages.eventChannel')}
                </button>
                {chatError && <p className="mt-2 text-sm text-red-600">{chatError}</p>}
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-6">
              {isEventPast ? (
                <div className="bg-gray-100 rounded-lg p-4 text-center">
                  <p className="text-gray-600">{t('events.eventEnded')}</p>
                </div>
              ) : isEventFull ? (
                <div className="bg-red-100 rounded-lg p-4 text-center">
                  <p className="text-red-600">{t('events.eventFull')}</p>
                </div>
              ) : (
                <button
                  onClick={handlePurchaseClick}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  {event.price ? t('events.purchaseTicket') : t('events.reserveSpot')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPurchaseModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handlePurchaseSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {event.price ? t('events.purchaseTicket') : t('events.reserveSpot')}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowPurchaseModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </div>

                  {purchaseError && (
                    <div className="mb-4 rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">{purchaseError}</div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        {t('common.email')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={purchaseData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                          {t('common.firstName')} *
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={purchaseData.firstName}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                          {t('common.lastName')} *
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={purchaseData.lastName}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                        {t('common.phoneNumber')}
                      </label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={purchaseData.phoneNumber}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                        {t('events.quantity')} *
                      </label>
                      <input
                        type="number"
                        id="quantity"
                        name="quantity"
                        min="1"
                        max={event.max_capacity - event.current_capacity}
                        value={purchaseData.quantity}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {event.price && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">{t('events.totalPrice')}:</span>
                          <span className="font-medium text-lg">
                            €{(event.price * purchaseData.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={purchasing}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {purchasing ? t('common.processing') : (event.price ? t('events.completePurchase') : t('events.confirmReservation'))}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPurchaseModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventDetailPage
